// frontend/src/pages/admin/AdminPortal.js
import { useState, useEffect } from "react";
import { LayoutDashboard, Users, UserPlus, ClipboardList, Database } from "lucide-react";
import Navbar from "../../components/navigation/Navbar";
import AdminGuard from "../../components/admin/AdminGuard";
import Overview from "./Overview";
import UsersList from "./UsersList";
import RegisterModal from "./RegisterModal";
import AuditLog from "./AuditLog";
import RetentionPolicies from "./RetentionPolicies";
import { getUserGroups } from "../../library/allauth";

const NAV_ITEMS = [
  { key: "overview",   label: "Overview",       icon: LayoutDashboard },
  { key: "users",      label: "Users",           icon: Users },
  { key: "register",   label: "Register User",   icon: UserPlus },
  { key: "retention",  label: "Retention Policies", icon: Database },
  { key: "audit-log",  label: "Audit Log",       icon: ClipboardList },
];

export default function AdminPortal() {
  const [activeSection, setActiveSection] = useState("overview");
  const [usersFilter, setUsersFilter]     = useState("all");
  // Track the logged-in admin's role so we can pass it to child components
  const [currentUserRole, setCurrentUserRole] = useState(null);
  const [currentUsername, setCurrentUsername] = useState(null);

  useEffect(() => {
    getUserGroups().then(res => {
      if (res.status === 200) {
        const groups = res.data?.groups ?? [];
        // Determine role priority: xroadz_admin > customer_admin > customer_user
        if (groups.includes("xroadz_admin")) {
          setCurrentUserRole("xroadz_admin");
        } else if (groups.includes("customer_admin")) {
          setCurrentUserRole("customer_admin");
        } else {
          setCurrentUserRole("customer_user");
        }
        setCurrentUsername(res.data?.username);
      }
    });
  }, []);

  const handleNavigate = (section, filter = "all") => {
    if (section === "users") setUsersFilter(filter);
    setActiveSection(section);
  };

  return (
    <AdminGuard>
      <Navbar />
      <div className="min-h-screen bg-gray-50 pt-14 flex">

        {/* Left Sub-Navigation */}
        <div className="w-56 bg-white border-r border-gray-200 min-h-screen flex-shrink-0">
          <div className="p-4 border-b border-gray-200">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
              Admin Panel
            </p>
            {/* Show which role is active */}
            {currentUserRole && (
              <p className="text-xs text-emerald-600 mt-1 font-medium capitalize">
                {currentUserRole === "xroadz_admin"
                  ? "Super Admin"
                  : currentUserRole === "customer_admin"
                  ? "Customer Admin"
                  : ""}
              </p>
            )}
          </div>
          <nav className="p-3 space-y-1">
            {NAV_ITEMS.map(item => {
              const Icon = item.icon;
              return (
                <button
                  key={item.key}
                  onClick={() => {
                    if (item.key === "users") setUsersFilter("all");
                    setActiveSection(item.key);
                  }}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors text-left ${
                    activeSection === item.key
                      ? "bg-emerald-50 text-emerald-700"
                      : "text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  <Icon size={17} />
                  {item.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-8">
          {activeSection === "overview" && (
            <Overview onNavigate={handleNavigate} />
          )}
          {activeSection === "users" && (
            <UsersList
              key={usersFilter}
              defaultFilter={
                activeSection === "users" && usersFilter !== "all"
                  ? usersFilter
                  : "all"
              }
              onRegister={() => setActiveSection("register")}
              currentUserRole={currentUserRole}
              currentUsername={currentUsername}
            />
          )}
          {activeSection === "register" && (
            <RegisterModal
              onSuccess={() => setActiveSection("users")}
              onCancel={() => setActiveSection("users")}
              currentUserRole={currentUserRole}
            />
          )}
          {activeSection === "retention" && (
            <RetentionPolicies />
          )}
          {activeSection === "audit-log" && (
            <AuditLog currentUserRole={currentUserRole} />
          )}
        </div>
      </div>
    </AdminGuard>
  );
}